import React, { useEffect, useRef } from "react";
import { Button, Modal, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CancelButtonLabel,
  ContentContainer,
  Divider,
  ErrorMessage,
  ModalContainer,
  RootContainer,
  SuccessMessageDescription,
  SuccessMessageTitle,
  Title,
  UploadButton,
  UploadButtonLabel,
  UploadButtonsContainer,
} from "@components/UploadTrainingModalCst/style";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CheckIcon from "@mui/icons-material/Check";
import { useTrainingCsvUpload } from "@hooks/useTrainingCsvUpload";

interface UploadTrainingModalCstProps {
  isOpen: boolean;
  onClose: () => void;
}

const UploadTrainingModalCst = ({
  isOpen,
  onClose,
}: UploadTrainingModalCstProps) => {
  const { t } = useTranslation(["skillBook-page"]);
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    selectedFile,
    errorMessage,
    isLoading,
    showSuccessMessage,
    reset,
    selectFile,
    confirmUpload,
    downloadTemplate,
  } = useTrainingCsvUpload();

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0]);
  };

  const SuccessMessage = (
    <Stack direction={"column"} gap={5}>
      <SuccessMessageTitle>
        {t("upload-training-modal.success-title")}
      </SuccessMessageTitle>
      <Stack
        direction="row"
        spacing={3}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <CheckIcon color={"success"} />
        <SuccessMessageDescription>
          {t("upload-training-modal.success-message")}
        </SuccessMessageDescription>
      </Stack>
      <Button
        variant={"contained"}
        onClick={onClose}
        sx={{
          alignSelf: "center",
        }}
      >
        {t("finish")}
      </Button>
    </Stack>
  );

  return (
    <Modal open={isOpen} onClose={onClose}>
      <RootContainer>
        <ModalContainer theme={theme}>
          {showSuccessMessage ? (
            SuccessMessage
          ) : (
            <>
              <Title variant={"h4"}>{t("upload-training-modal.title")}</Title>

              <Divider />

              <ContentContainer>
                <Typography
                  sx={{
                    fontsize: 16,
                  }}
                >
                  {t("upload-training-modal.description")}
                </Typography>
                <UploadButtonsContainer>
                  <input
                    type={"file"}
                    accept={".csv,.CSV"}
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <Button
                    endIcon={<FileDownloadIcon />}
                    variant={"outlined"}
                    onClick={downloadTemplate}
                    disabled={isLoading}
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("upload-training-modal.download-template-cta")}
                  </Button>
                  <UploadButton
                    endIcon={
                      <FileUploadIcon
                        sx={{ color: theme.palette.text.primary }}
                      />
                    }
                    variant={"text"}
                    onClick={onUploadClick}
                    disabled={isLoading}
                  >
                    <UploadButtonLabel>
                      {selectedFile?.name ??
                        t("upload-training-modal.upload-cta")}
                    </UploadButtonLabel>
                  </UploadButton>
                </UploadButtonsContainer>
              </ContentContainer>

              {errorMessage && (
                <ErrorMessage
                  dangerouslySetInnerHTML={{ __html: errorMessage }}
                />
              )}

              <Divider />

              <Stack direction={"row"} spacing={2}>
                <Button variant={"text"} onClick={onClose}>
                  <CancelButtonLabel>
                    {t("upload-training-modal.cancel-cta")}
                  </CancelButtonLabel>
                </Button>
                <Button
                  disabled={!selectedFile || isLoading}
                  variant={"contained"}
                  onClick={confirmUpload}
                >
                  {t("upload-training-modal.confirm-cta")}
                </Button>
              </Stack>
            </>
          )}
        </ModalContainer>
      </RootContainer>
    </Modal>
  );
};

export default UploadTrainingModalCst;
